/**
 * BetterStack Logging Integration
 * 
 * Captures console messages and sends them to BetterStack for centralized logging.
 * Based on BetterStack integration for Unity.
 * 
 * Usage:
 * 
 * // Initialize as early as possible in your application
 * import { betterStackLogger } from './common/src/betterStackLogger';
 * 
 * betterStackLogger.init({
 *   sourceToken: process.env.BETTERSTACK_SOURCE_TOKEN || '',
 *   appName: 'werewolf-app',
 *   logLevel: 'error' // Optional, defaults to 'error'
 * });
 * 
 * Primary Columns for BetterStack (https://logs.betterstack.com/team/____/sources/____/advanced-settings):
 * {level} {app} {shortSessionId} {logId} {message}
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  message: string;
  level: LogLevel;
  app: string;
  sessionId: string;
  shortSessionId: string;
  logId: string;
  stackTrace?: string;
}

interface BetterStackConfig {
  sourceToken: string;
  appName: string;
  logLevel?: LogLevel;
}

class BetterStackLogger {
  private sourceToken: string = '';
  private appName: string = '';
  private logLevelThreshold: LogLevel = 'error';
  private sessionId: string = '';
  private shortSessionId: string = '';
  private logCount: number = 0;
  private logBuffer: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isInitialized: boolean = false;
  private isFlushing: boolean = false;
  private loggingDisabled: boolean = false;
  private originalConsole: {
    log: typeof console.log;
    info: typeof console.info;
    warn: typeof console.warn;
    error: typeof console.error;
    debug: typeof console.debug;
  };

  // Configuration
  private readonly BETTERSTACK_URL = 'https://in.logs.betterstack.com/';
  private readonly MAX_BUFFER_SIZE = 5 * 1024 * 1024; // 5MB (BetterStack has 10MB limit)
  private readonly FLUSH_DELAY_MS = 2000; // 2 seconds
  private readonly LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor() {
    // Generate session ID (20 characters, no dashes)
    this.sessionId = this.generateSessionId();
    this.shortSessionId = this.sessionId.substring(0, 4);

    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug.bind(console),
    };
  }

  /**
   * Initialize BetterStack logging
   * @param config Configuration object with sourceToken, appName, and optional logLevel
   */
  public init(config: BetterStackConfig): void {
    // If no source token, don't initialize
    if (!config.sourceToken) {
      return;
    }

    // Clean up any existing initialization
    this.flush();
    this.restoreConsole();

    this.sourceToken = config.sourceToken;
    this.appName = config.appName;
    this.logLevelThreshold = config.logLevel || 'error';
    this.isInitialized = true;

    // Override console methods
    this.overrideConsole();

    // Log initialization message
    this.originalConsole.info('****************************************************************************************************');
    this.originalConsole.info(`BetterStack Logger Initialized - App: '${this.appName}' SessionId: '${this.shortSessionId}'`);
  }

  /**
   * Generate a unique session ID (20 characters)
   */
  private generateSessionId(): string {
    // Generate a UUID-like string and remove dashes
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    return uuid.replace(/-/g, '').substring(0, 20);
  }

  /**
   * Check if a log level should be logged based on the threshold
   */
  private shouldLog(level: LogLevel): boolean {
    return this.LOG_LEVEL_PRIORITY[level] >= this.LOG_LEVEL_PRIORITY[this.logLevelThreshold];
  }

  /**
   * Override console methods to capture logs
   */
  private overrideConsole(): void {
    const self = this;

    console.log = (...args: any[]) => {
      self.originalConsole.log(...args);
      self.captureLog('info', args);
    };

    console.info = (...args: any[]) => {
      self.originalConsole.info(...args);
      self.captureLog('info', args);
    };

    console.warn = (...args: any[]) => {
      self.originalConsole.warn(...args);
      self.captureLog('warn', args);
    };

    console.error = (...args: any[]) => {
      self.originalConsole.error(...args);
      self.captureLog('error', args, this.captureStackTrace());
    };

    console.debug = (...args: any[]) => {
      self.originalConsole.debug(...args);
      self.captureLog('debug', args);
    };
  }

  /**
   * Restore original console methods
   */
  private restoreConsole(): void {
    console.log = this.originalConsole.log;
    console.info = this.originalConsole.info;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.debug = this.originalConsole.debug;
  }

  /**
   * Capture a log message
   */
  private captureLog(level: LogLevel, args: any[], stackTrace?: string): void {
    if (!this.isInitialized || this.loggingDisabled || !this.shouldLog(level)) {
      return;
    }

    // Convert arguments to string
    const message = args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.message;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');

    // Create log entry
    const entry: LogEntry = {
      message: this.cleanForJSON(message),
      level,
      app: this.appName,
      sessionId: this.sessionId,
      shortSessionId: this.shortSessionId,
      logId: (++this.logCount).toString().padStart(4, '0'),
    };

    if (stackTrace) {
      entry.stackTrace = this.cleanForJSON(stackTrace);
    }

    // Add to buffer
    this.logBuffer.push(entry);

    // Check if we should flush
    const bufferSize = this.estimateBufferSize();
    if (bufferSize > this.MAX_BUFFER_SIZE) {
      this.flush();
    } else {
      this.scheduleFlush();
    }
  }

  /**
   * Capture current stack trace
   */
  private captureStackTrace(): string {
    const err = new Error();
    return err.stack || '';
  }

  /**
   * Estimate the size of the current buffer in bytes
   */
  private estimateBufferSize(): number {
    return JSON.stringify(this.logBuffer).length;
  }

  /**
   * Schedule a delayed flush
   */
  private scheduleFlush(): void {
    if (this.flushTimer !== null || this.logBuffer.length === 0) {
      return;
    }

    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.FLUSH_DELAY_MS);
  }

  /**
   * Flush all buffered logs to BetterStack
   */
  public flush(): void {
    if (this.isFlushing || this.logBuffer.length === 0) {
      return;
    }

    // Clear the flush timer
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer as NodeJS.Timeout);
      this.flushTimer = null;
    }

    // Get all buffered entries and clear the buffer
    const entries = [...this.logBuffer];
    this.logBuffer = [];
    this.isFlushing = true;

    // Send to BetterStack
    this.sendLogEntries(entries);
  }

  /**
   * Send log entries to BetterStack
   */
  private async sendLogEntries(entries: LogEntry[]): Promise<void> {
    try {
      // Convert entries to BetterStack format
      const payload = entries.map((entry) => {
        const logObj: any = {
          message: entry.message,
          level: entry.level,
          app: entry.app,
          sessionId: entry.sessionId,
          shortSessionId: entry.shortSessionId,
          logId: entry.logId,
        };

        if (entry.stackTrace) {
          // Add ANSI color code for red text in stack traces
          logObj.message = '\u001b[31m[StackTrace]\u001b[0m ' + logObj.message;
          logObj.stackTrace = entry.stackTrace;
        }

        return logObj;
      });

      const body = JSON.stringify(payload);

      // Send to BetterStack
      const response = await fetch(this.BETTERSTACK_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.sourceToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        const responseText = await response.text();
        this.loggingDisabled = true;
        this.originalConsole.error(
          `BetterStack logging failed: ${response.status} ${response.statusText} ${responseText}`
        );
        this.loggingDisabled = false;
      }
    } catch (error) {
      this.loggingDisabled = true;
      this.originalConsole.error('BetterStack logging error:', error);
      this.loggingDisabled = false;
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Clean a string for JSON encoding
   */
  private cleanForJSON(str: string): string {
    if (!str || str.length === 0) {
      return '';
    }

    let result = '';
    for (let i = 0; i < str.length; i++) {
      const c = str[i];
      switch (c) {
        case '\\':
        case '"':
          result += '\\' + c;
          break;
        case '\b':
          result += '\\b';
          break;
        case '\t':
          result += '\\t';
          break;
        case '\n':
          result += '\\n';
          break;
        case '\f':
          result += '\\f';
          break;
        case '\r':
          result += '\\r';
          break;
        default:
          if (c < ' ') {
            const hex = c.charCodeAt(0).toString(16).padStart(4, '0');
            result += '\\u' + hex;
          } else {
            result += c;
          }
          break;
      }
    }
    return result;
  }

  /**
   * Shutdown the logger and restore console
   */
  public shutdown(): void {
    this.flush();
    this.restoreConsole();
    this.isInitialized = false;
  }
}

// Export singleton instance
export const betterStackLogger = new BetterStackLogger();
